import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { QR, QrActionButton, qrDialogPaperSx, QrModalHeader } from "../../../qrCode/components/qrUi";
import { FieldSelect } from "../../../../components/selections/fieldInputs";
import { NameCell, ChipList, PersonChip, MutedText } from "../leadUi";
import Slide from "@mui/material/Slide";
import DataTableWithCheckbox from "../../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { addNewAssigningLeadsThunk, getLeadManagementThunk, updateUnAssigningLeadsThunk, getMyTaskThunk } from '../../leadMangementThunk';
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../../config/appRoutes";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function AssignAndUnAssignModal({ show, handleClose, stateData, selectionWiseLeadDetails }) {

  const dispatch = useDispatch();
  const { getEmployees } = useSelector((state) => state.employees);
  const [selectedRows, setSelectedRows] = useState([]);
  const { status } = useSelector((state) => state.leadManagement);
  const { selectedValue } = stateData;
  const isAssign = selectedValue === "1";
  const navigate = useNavigate();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [state, setState] = useState({
    selectedEmployeeId: "",
    errors: {},
  });

  const { selectedEmployeeId, errors } = state;


  const columns = [
    { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
    { accessorKey: "name", header: "Name", Cell: ({ row }) => <NameCell name={row.original.name} /> },
    // { accessorKey: "email", header: "Email" },
    ...(selectedValue !== "1" && selectedValue !== "3" ? [{ accessorKey: "employee_name", header: "Employee Name", Cell: ({ row }) => <PersonChip name={row.original.employee_name} /> }] : []),
    ...(selectedValue !== "3" ? [
      { accessorKey: "branch_names", header: "Branch", Cell: ({ row }) => <MutedText value={row.original.branch_names} /> },
      { accessorKey: "department_names", header: "Department", Cell: ({ row }) => <ChipList value={row.original.department_names} color={QR.blue} /> }
    ] : []),
    { accessorKey: "tag_names", header: "Tag", Cell: ({ row }) => <ChipList value={row.original.tag_names} /> },
  ];

  const handleSingleSelectChange = (name, selectedValue) => {
    setState((prev) => ({
      ...prev,
      [name]: selectedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validations = [
      ...(selectedValue === "1"
        ? [{ valid: !!selectedEmployeeId, message: "Employee type is required", field: "selectedEmployeeId" }]
        : []),
    ];

    for (let rule of validations) {
      if (!rule.valid) {
        setSnackbar({
          open: true,
          message: rule.message,
          severity: "error",
        });

        setState((prev) => ({
          ...prev,
          errors: { ...prev.errors, [rule.field]: rule.message },
        }));
        return;
      }
    }

    try {
      let result;
      if (selectedValue === "1") {
        result = await dispatch(
          addNewAssigningLeadsThunk({
            credentials: {
              wa_contact_ids: selectedRows.map((item) => item.wa_contact_id),
              assigned_emp_id: selectedEmployeeId
            }
          })
        );
      } else {
        result = await dispatch(
          updateUnAssigningLeadsThunk({
            credentials: {
              wa_contact_ids: selectedRows.map((item) => item.wa_contact_id),
            }
          })
        );
      }

      if (result.meta.requestStatus === "fulfilled") {
        setSnackbar({
          open: true,
          message: result.payload.message,
          severity: "success",
        });
        await dispatch(getLeadManagementThunk({ type: "getLeads" }));

        if (selectedValue === "1") {
          navigate(APP_ROUTES.LEADMANAGEMENT.MY_TASK);
          dispatch(getMyTaskThunk({ type: "getTask" }))
        }

        handleClose();
      } else {
        setSnackbar({
          open: true,
          message: result.payload,
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Server error. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      keepMounted
      PaperProps={{ sx: qrDialogPaperSx }}
    >
      <QrModalHeader
        icon={isAssign ? <GroupAddOutlinedIcon /> : <PersonRemoveOutlinedIcon />}
        title={isAssign ? "Assign Leads" : "UnAssign Leads"}
        subtitle={isAssign ? "Select the leads, then choose the employee to assign them to" : "Select the leads to remove from their employee"}
        onClose={() => handleClose()}
      />

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <DataTableWithCheckbox
          show={show}
          columns={columns}
          data={selectionWiseLeadDetails || []}
          enableRowSelection={true}
          onSelectionChange={setSelectedRows}
          selectedRows={selectedRows}
          searchFirst
          emptyMessage={isAssign ? "No unassigned leads to assign." : "No assigned leads."}
        />
      </DialogContent>

      {/* Action bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 2,
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          borderTop: `1px solid ${QR.border}`,
          bgcolor: "#fafbfc",
        }}
      >
        <Box sx={{ alignSelf: "center", mr: "auto" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: selectedRows.length ? "#0f172a" : "#94a3b8" }}>
            {selectedRows.length} lead{selectedRows.length === 1 ? "" : "s"} selected
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
            {selectedRows.length ? (isAssign ? "Choose an employee and assign" : "Ready to un assign") : "Tick leads in the table to continue"}
          </Typography>
        </Box>

        {selectedRows.length > 0 && isAssign && (
          <Box sx={{ width: { xs: "100%", sm: 280 } }}>
            <FieldSelect
              name="selectedEmployeeId"
              label="Assign To"
              options={getEmployees.map((item) => ({
                value: item.id,
                label: item.employee_name,
              })) || []}
              value={selectedEmployeeId}
              onChange={handleSingleSelectChange}
              error={!!errors.selectedEmployeeId}
              icon={<PersonOutlineIcon />}
              placeholder="Select employee"
              required
            />
          </Box>
        )}

        {selectedRows.length > 0 && (
          <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" }, height: 42 } }}>
            <QrActionButton
              variant="solid"
              color={isAssign ? QR.blue : "#fc8006"}
              gradientTo={isAssign ? "#3b82f6" : "#fb923c"}
              icon={isAssign ? <GroupAddOutlinedIcon sx={{ fontSize: 18 }} /> : <PersonRemoveOutlinedIcon sx={{ fontSize: 18 }} />}
              label={`${isAssign ? "Assign Leads" : "Un Assign"}`}
              loading={status === "loading"}
              loadingLabel="Submitting.."
              onClick={handleSubmit}
            />
          </Box>
        )}
      </Box>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Dialog>
  );
}
